import { Component, OnInit, Input, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { ReportService } from '../../shared/report-service';
import { DataQueryDto } from '../../shared/dto/data-query.dto';
import { QueryDefinitionDto } from '../../shared/dto/query-definition.dto';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { DatagridService, NotificationService, PagedResultGeneric, PagedSearch, TranslationService, UtilService } from 'src/app/shared';
import { QueryToolSaveQueryModalComponent } from '../query-tool-save-query-modal/query-tool-save-query-modal.component';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { ProfileSearchParameterService } from 'src/app/profiles/shared/profile-search-parameter.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ProfileSearchComponentModel } from 'src/app/profiles/search/profile-search-component.model';
import { ColumnDefinitionDto } from '../../shared/dto/column-definition.dto';
import { ProfileSearchV2ParameterService } from 'src/app/profiles/shared/profile-search-v2-parameter.service';
import { ConfigServiceService } from 'src/app/config/shared/config-service.service';
import { UserService } from 'src/app/users/shared/user.service';
import { UserRead } from 'src/app/users/shared/user-read.model';

@Component({
  selector: 'wl-query-tool-view-results',
  templateUrl: './query-tool-view-results.component.html',
  styleUrls: ['./query-tool-view-results.component.less'],
  providers: [ProfileSearchParameterService],
    standalone: false
})
export class QueryToolViewResultsComponent extends LazySelectableTableComponent<PagedSearch, any> implements OnInit, OnChanges {
  @ViewChild(QueryToolSaveQueryModalComponent) _queryToolSaveQueryModal: QueryToolSaveQueryModalComponent;

  columnNames: Array<string>;
  queryResults = new Array<any>();
  loading: boolean = true;
  exporting: boolean = false;
  totalCount: number;
  user: UserRead;
  @Input() dataQuery: DataQueryDto;
  @Input() queryDefinition: QueryDefinitionDto;
  @Input() availableFields: Array<ColumnDefinitionDto>;
  fieldTypes = {}; //annonymous because we want to be able to lookup the types by name through binding
  useV2ProfileSelector: boolean = false;

  constructor(
    private _reportService: ReportService,
    datagridService: DatagridService,
    private _notificationService: NotificationService,
    private _navigationService: NavigationService,
    private _profileSearchParameterService: ProfileSearchParameterService,
    private _profileSearchV2ParameterService: ProfileSearchV2ParameterService,
    private _configService: ConfigServiceService,
    private _userService: UserService,
    private _utilService: UtilService,
    private _translationService: TranslationService
  ) {
    super('id', datagridService);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.availableFields) {
      this.fieldTypes = this.availableFields.reduce(function (map, obj) {
        map[obj.ReplacementName] = obj.ColumnType;
        return map;
      }, {});
    }
  }

  ngOnInit(): void {
    this._userService.getCurrentUser().then((user) => {
      this.user = user;
      // Check whether to set V2 or original search results
      this._configService.checkForProfileSearchConfig(this.user.UserNum.toString()).then((hasProfileSelectorConfig) => {
        this.useV2ProfileSelector = hasProfileSelectorConfig;
      });
    });
    this.searchResult = { PageSize: 20, CurrentPageNumber: 1 } as PagedResultGeneric<any>;
    this.searchParams = { PageNumber: 1, PageSize: 20 } as PagedSearch;
  }

  runQuery() {
    this.loading = true;
    this._reportService
      .runQuery(this.dataQuery)
      .then((result) => {
        if (result.TotalCount > 0) {
          if (result.TotalCount > 1000 && this.searchParams.PageSize <= 0) {
            this._notificationService.warn(this._translationService.getInstant('reports.query-tool.view-results.warning'));
          }

          //Parse the results from the JSON string.
          this.queryResults = JSON.parse(result.Results);
          this.columnNames = Object.keys(this.queryResults[0]);
        }
        this.totalCount = result.TotalCount;
        this.search();
      })
      .catch((error: HttpErrorResponse) => {
        if (error.status == 408 || error.status == 503) {
          this._notificationService.translateError('reports.query-tool.view-results.timeout-error');
        } else {
          this._notificationService.httpError(error);
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  fetchResults(): Promise<PagedResultGeneric<any>> {
    const currentPageNum = this.searchParams.PageNumber - 1;
    let result = [];
    if (this.searchParams.OrderBy) {
      let orderByStrings = this.searchParams.OrderBy.split(':');
      let sortedProperty = orderByStrings[0];
      let sortOrder = orderByStrings[1];
      if (sortOrder == 'asc') {
        this.queryResults = this._utilService.sortByStringAsc(this.queryResults, sortedProperty);
      } else {
        this.queryResults = this._utilService.sortByStringDesc(this.queryResults, sortedProperty);
      }
    }

    let startIndex = currentPageNum * this.searchParams.PageSize;
    let endIndex = startIndex + this.searchParams.PageSize;
    result = this.queryResults.slice(startIndex, endIndex);

    if (this.searchParams.PageSize > 1000 && this.totalCount > 1000) {
      this._notificationService.warn(this._translationService.getInstant('reports.query-tool.view-results.warning'));
    }

    return Promise.resolve({
      PageSize: this.searchParams.PageSize,
      CurrentPageNumber: this.searchParams.PageNumber,
      TotalCount: this.totalCount,
      TotalPages: ((this.queryResults.length / 10) | 0) + 1,
      Result: result,
    } as PagedResultGeneric<any>);
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  openSaveDialog() {
    this.dataQuery.DefinitionDto = this.queryDefinition;
    this._queryToolSaveQueryModal.showDialog(this.dataQuery);
  }

  exportResults() {
    this.exporting = true;
    this._reportService
      .exportDataQuery(this.dataQuery)
      .then((csvUrl) => {
        this._navigationService.popupSafeNavigate(csvUrl);
      })
      .catch((error: HttpErrorResponse) => {
        if (error.status == 408 || error.status == 503) {
          this._notificationService.translateError('reports.query-tool.view-results.timeout-error');
        } else {
          this._notificationService.httpError(error);
        }
      })
      .finally(() => {
        this.exporting = false;
      });
  }

  openProfileSelector() {
    if (!/(profile_id)|(profile id)|(profileid)/i.test(this.dataQuery.Sql)) {
      const errMsg = this._translationService.getInstant('reports.saved-queries.profile-selector-not-supported');
      this._notificationService.error(errMsg);
      return;
    }
    this._reportService
      .getDataQueryProfileIds(this.dataQuery)
      .then((profileIds: Array<number>) => {
        const profileids = profileIds;
        const profileSearchModel = new ProfileSearchComponentModel();
        if (profileids && profileids.length) {
          profileSearchModel.searchParams.PermitProfiles = profileids;
          this._profileSearchV2ParameterService.setSearchLoadOptions({ RunOnLoad: true, HideAdvancedParams: true, FromNavigation: true });
          if (this.useV2ProfileSelector) {
            // Having both V2 and original results stored locally will mess up opening the V2 Profile Selector so clear it out.
            this._profileSearchParameterService.clearProfileSearchResults();
            this._profileSearchV2ParameterService.setProfileSearchResults(profileSearchModel);
          } else {
            this._profileSearchParameterService.setProfileSearchResults(profileSearchModel);
          }
          this._navigationService.navigateToProfileFinderNewTab();
        } else {
          this._notificationService.translateError('reports.saved-queries.no-results');
        }
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status == 408 || err.status == 503) {
          this._notificationService.translateError('reports.query-tool.view-results.timeout-error');
        } else {
          this._notificationService.httpError(err);
        }
      });
  }
}
